import React, { ComponentType, useMemo, useCallback } from 'react';
import {
  useQuery,
  useMutation,
  useSubscription,
  useApolloClient,
  OperationVariables,
  DocumentNode,
  TypedDocumentNode,
} from '@apollo/client';

type QueryConfig<TData, TVariables extends OperationVariables> = {
  name?: string;
  options?: ((props: Record<string, unknown>) => Record<string, unknown>) | Record<string, unknown>;
  props?: (result: {
    ownProps: Record<string, unknown>;
    data?: TData;
    loading: boolean;
    error?: Error;
    refetch: () => void;
    mutate?: (opts: { variables?: TVariables }) => Promise<unknown>;
  }) => Record<string, unknown>;
  skip?: (props: Record<string, unknown>) => boolean;
};

function resolveOptions<T extends Record<string, unknown>>(
  options: QueryConfig<unknown, OperationVariables>['options'],
  props: Record<string, unknown>,
): T {
  if (typeof options === 'function') {
    return options(props) as T;
  }
  return (options ?? {}) as T;
}

function getOperationType(document: DocumentNode): 'query' | 'mutation' | 'subscription' {
  const definition = document.definitions.find((d) => d.kind === 'OperationDefinition');
  if (definition && 'operation' in definition) {
    return definition.operation as 'query' | 'mutation' | 'subscription';
  }
  return 'query';
}

function wrapWithDisplayName<P>(
  WrappedComponent: ComponentType<P>,
  Wrapper: ComponentType<P>,
): ComponentType<P> {
  Wrapper.displayName = `graphql(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return Wrapper;
}

function buildQueryDataProp(queryResult: ReturnType<typeof useQuery>) {
  return {
    loading: queryResult.loading,
    error: queryResult.error,
    networkStatus: queryResult.networkStatus,
    refetch: queryResult.refetch,
    fetchMore: queryResult.fetchMore,
    startPolling: queryResult.startPolling,
    stopPolling: queryResult.stopPolling,
    subscribeToMore: queryResult.subscribeToMore,
    variables: queryResult.variables,
    called: queryResult.called,
    client: queryResult.client,
    ...(queryResult.data ?? {}),
  };
}

function buildSubscriptionDataProp(subscriptionResult: ReturnType<typeof useSubscription>) {
  return {
    loading: subscriptionResult.loading,
    error: subscriptionResult.error,
    ...(subscriptionResult.data ?? {}),
  };
}

function withQueryGraphql<TData, TVariables extends OperationVariables>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  config: QueryConfig<TData, TVariables>,
) {
  return <P extends Record<string, unknown>>(WrappedComponent: ComponentType<P>) => {
    function QueryWrapper(props: P) {
      const resolvedOptions = resolveOptions<Record<string, unknown>>(config.options, props);
      const variables = (resolvedOptions.variables ?? {}) as TVariables;
      const skip =
        (typeof config.skip === 'function' ? config.skip(props) : false) ||
        resolvedOptions.skip === true;

      const queryResult = useQuery<TData, TVariables>(document, {
        ...resolvedOptions,
        variables,
        skip,
      });

      const injectedProps = useMemo(() => {
        const name = config.name ?? 'data';
        const dataProp = buildQueryDataProp(queryResult);

        if (config.props) {
          return config.props({
            ownProps: props,
            [name]: dataProp,
          } as Parameters<NonNullable<typeof config.props>>[0]);
        }

        return { [name]: dataProp };
      }, [
        props,
        queryResult.data,
        queryResult.loading,
        queryResult.error,
        queryResult.networkStatus,
        queryResult.refetch,
        queryResult.fetchMore,
        queryResult.subscribeToMore,
      ]);

      return <WrappedComponent {...props} {...injectedProps} />;
    }

    return wrapWithDisplayName(WrappedComponent, QueryWrapper);
  };
}

function withMutationGraphql<TData, TVariables extends OperationVariables>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  config: QueryConfig<TData, TVariables>,
) {
  return <P extends Record<string, unknown>>(WrappedComponent: ComponentType<P>) => {
    function MutationWrapper(props: P) {
      const resolvedOptions = resolveOptions<Record<string, unknown>>(config.options, props);

      const [mutateFn, mutationResult] = useMutation<TData, TVariables>(document, {
        refetchQueries: resolvedOptions.refetchQueries as string[] | undefined,
        awaitRefetchQueries: resolvedOptions.awaitRefetchQueries as boolean | undefined,
      });

      const mutate = useCallback(
        (opts: Record<string, unknown>) => mutateFn(opts as Parameters<typeof mutateFn>[0]),
        [mutateFn],
      );

      const injectedProps = useMemo(() => {
        if (config.props) {
          return config.props({
            ownProps: props,
            loading: mutationResult.loading,
            error: mutationResult.error,
            refetch: () => {},
            mutate,
          });
        }

        return { mutate, loading: mutationResult.loading };
      }, [props, mutationResult.loading, mutationResult.error, mutate]);

      return <WrappedComponent {...props} {...injectedProps} />;
    }

    return wrapWithDisplayName(WrappedComponent, MutationWrapper);
  };
}

function withSubscriptionGraphql<TData, TVariables extends OperationVariables>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  config: QueryConfig<TData, TVariables>,
) {
  return <P extends Record<string, unknown>>(WrappedComponent: ComponentType<P>) => {
    function SubscriptionWrapper(props: P) {
      const resolvedOptions = resolveOptions<Record<string, unknown>>(config.options, props);
      const variables = (resolvedOptions.variables ?? {}) as TVariables;
      const skip =
        (typeof config.skip === 'function' ? config.skip(props) : false) ||
        resolvedOptions.skip === true;

      const subscriptionResult = useSubscription(document, {
        ...resolvedOptions,
        variables,
        skip,
      });

      const injectedProps = useMemo(() => {
        const name = config.name ?? 'data';
        const dataProp = buildSubscriptionDataProp(subscriptionResult);

        if (config.props) {
          return config.props({
            ownProps: props,
            [name]: dataProp,
          } as Parameters<NonNullable<typeof config.props>>[0]);
        }

        return { [name]: dataProp };
      }, [
        props,
        subscriptionResult.data,
        subscriptionResult.loading,
        subscriptionResult.error,
      ]);

      return <WrappedComponent {...props} {...injectedProps} />;
    }

    return wrapWithDisplayName(WrappedComponent, SubscriptionWrapper);
  };
}

export function graphql<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  config: QueryConfig<TData, TVariables> = {},
) {
  const operationType = getOperationType(document);

  if (operationType === 'mutation') {
    return withMutationGraphql(document, config);
  }
  if (operationType === 'subscription') {
    return withSubscriptionGraphql(document, config);
  }
  return withQueryGraphql(document, config);
}

export function compose<P>(...funcs: Array<(c: ComponentType<P>) => ComponentType<P>>) {
  return (component: ComponentType<P>) =>
    funcs.reduceRight((acc, fn) => fn(acc), component);
}

export function withApollo<P>(WrappedComponent: ComponentType<P & { client: ReturnType<typeof useApolloClient> }>) {
  function WithApolloWrapper(props: P) {
    const apolloClient = useApolloClient();
    return <WrappedComponent {...props} client={apolloClient} />;
  }
  return WithApolloWrapper;
}

export { ApolloProvider } from '@apollo/client';
